import { Row, Dropdown, Menu, Icon } from 'antd';
import React, { useState, ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Search } from '@arcflight/tf-component-library';
import { DashboardState } from '../../models';
import type {
  Aircraft,
  AircraftStatusFilters,
  FlightStatusFilters,
  ServiceabilityFilters,
  SimpleAircraft,
} from '../../models/aircraft';
import { add, addFavouriteAircraftId, removeFavouriteAircraftId } from '../../models/userSettings/actions';
import { saveAllAircraft } from '../../models/aircraft/actions';
import PageHeaderWrapper from '../../components/PageHeaderWrapper';
import { addToFavourites, removeFromFavourites } from '../../services/api';
import { queryAircraftByOperator, querySimpleAircraftByOperator, queryFavouriteAircraft } from '../../services/apiNew';
import TFFilterButton from '../../components/TFFilterButton';
import EmptyState from '../../components/EmptyState/EmptyState';
import FleetIcon from '../../assets/topNav/title-fleet.svg';
import EmptyStateAircraft from '../../assets/emptyState/empty-state-aircraft-not-found.svg';
import TypedAircraftCard from '../../components/AircraftCard/TypedAircraftCard';
import { addToast, ToastCategories, ToastTypes } from '../../models/toasts';
import TableGridToggle from '../../components/TableGridToggle/TableGridToggle';
import usePersistedFilter from '../../hooks/usePersistedFilter/usePersistedFilter';
import type { Theme } from '../../providers/CustomThemeProvider';
import { applyAircraftFilters, FleetRegistrationSort } from './aircraftFiltering';

interface FleetFilterState {
  serviceabilityFilters: ServiceabilityFilters;
  aircraftStatusFilters: AircraftStatusFilters;
  flightStatusFilters: FlightStatusFilters;
  operatorFilter: string[];
  typeFilter: string[];
}

const DEFAULT_FLEET_FILTERS: FleetFilterState = {
  serviceabilityFilters: {
    ok: false,
    critical: false,
    overdue: false,
    unknown: false,
  },
  aircraftStatusFilters: {
    active: false,
    archived: false,
    deleted: false,
    demo: false,
    locked: false,
    'mel only': false,
    setup: false,
  },
  flightStatusFilters: {
    on_ground: false,
    in_flight: false,
  },
  operatorFilter: [],
  typeFilter: [],
};

function isFiltered(state: FleetFilterState, search: string): boolean {
  const hasFilterSelections = Object.values(state.serviceabilityFilters).some(Boolean)
    || Object.values(state.aircraftStatusFilters).some(Boolean)
    || Object.values(state.flightStatusFilters).some(Boolean)
    || state.operatorFilter.length > 0
    || state.typeFilter.length > 0;

  return hasFilterSelections || search.trim().length > 0;
}

const OperatorDiv = styled.div`
  margin-bottom: 24px;
`;

const CardsDiv = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr 1fr 1fr;
  @media (max-width: 1600px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 976px) {
    grid-template-columns: 1fr;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 24px;
  margin-bottom: 24px;

  @media screen and (max-width: 1440px) {
  }
  @media screen and (max-width: 991px) {
    row-gap: 8px;
    justify-content: center;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

const FilterText = styled.span`
  @media screen and (max-width: 991px) {
    display: none;
  }
`;

/* Left | right side by side; when narrow, right wraps under. align-items: flex-start lines up tops. */
const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 20px;
  width: 100%;
  min-width: 0;
`;

const LeftFilterBarBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  flex: 1 1 320px;
  min-width: 0;
  max-width: 100%;
`;

/* Line 1: label, pills, search. Line 2: clear all. */
const FilterTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  row-gap: 24px;
  min-width: 0;
  width: 100%;
  @media screen and (max-width: 991px) {
    row-gap: 8px;
    justify-content: center;
  }
`;

const RightFilterBarBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
  row-gap: 24px;
  margin-left: auto;

  @media screen and (max-width: 991px) {
    row-gap: 8px;
    justify-content: flex-start;
    width: 100%;
    margin-left: 0;
  }
`;

const SearchWrapper = styled.div`
  flex: 1 1 200px;
  min-width: 140px;
  max-width: 100%;

  & > * {
    min-width: 0;
    max-width: 100%;
  }

  div,
  img,
  button,
  input {
    box-sizing: revert;
    line-height: normal;
  }
`;

const ClearAllFiltersLink = styled.button<{ theme: Theme }>`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${({ theme }): string => theme.colours.blue};
  font-size: 14px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: fit-content;
  max-width: 100%;
  -webkit-tap-highlight-color: transparent;

  text-decoration: none;

  &:hover:not(:disabled) {
    text-decoration: none;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }): string => theme.colours.grayBlue};
    opacity: 0.55;
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    border: none;
    box-shadow: none;
  }
`;

const SortByLabel = styled.span`
  margin-right: 4px;
  font-weight: 600;
`;

const FLEET_FILTER_PAGE_KEY = 'fleet';

const Fleet: React.FC = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const {
    userSettings: { details },
    userSettings: {
      details: { operators },
    },
  } = useSelector((state: DashboardState) => state);

  const { filters, setFilters, clearFilters } = usePersistedFilter<FleetFilterState>(
    FLEET_FILTER_PAGE_KEY,
    // details is defined or would have failed above when useSelector was called
    details.id,
    DEFAULT_FLEET_FILTERS,
  );

  const {
    serviceabilityFilters,
    aircraftStatusFilters,
    flightStatusFilters,
    operatorFilter,
    typeFilter,
  } = filters;

  const updateFilter = (partial: Partial<FleetFilterState>): void => {
    setFilters({ ...filters, ...partial });
  };

  const [searchText, setSearchText] = useState<string>('');
  const [hasSetAircraftMap, setHasSetAircraftMap] = useState<boolean>(false);
  const [resetSearch, setResetSearch] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<FleetRegistrationSort>('asc');
  const [filteredAircraftQueries, setfilteredAircraftQueries] = useState<Aircraft[][] | []>();
  const [filteredFavourites, setFilteredFavourites] = useState<Aircraft[]>();
  const prevSearchTextRef = useRef<string | undefined>();
  const prevServiceabilityRef = useRef<typeof serviceabilityFilters | undefined>();
  const prevAircraftStatusRef = useRef<typeof aircraftStatusFilters | undefined>();
  const prevFlightStatusRef = useRef<typeof flightStatusFilters | undefined>();
  const prevOperatorRef = useRef<string[] | undefined>();
  const prevTypeRef = useRef<string[] | undefined>(typeFilter);
  const prevSortByRef = useRef<FleetRegistrationSort | undefined>();
  const prevData = useRef<Aircraft[] | undefined>();
  const prevFleetStyleRef = useRef<string | undefined>();
  const hasMountedRef = useRef<boolean>(false);

  const fleetQueryOptions = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  } as const;

  const { isSuccess, data } = useQuery({
    queryKey: ['aircraft', 'favourites'],
    queryFn: () => queryFavouriteAircraft(),
    refetchInterval: 60000,
    ...fleetQueryOptions,
  });

  const simpleAircraftQueries = useQueries({
    queries: operators.map((operator) => ({
      queryKey: ['getSimpleAircraftByOperators', operator.id],
      queryFn: () => querySimpleAircraftByOperator(operator.id),
      ...fleetQueryOptions,
    })),
  });

  const aircraftQueries = useQueries({
    queries: operators.map((operator) => ({
      queryKey: ['aircraft', operator.id],
      queryFn: () => queryAircraftByOperator(operator.id),
      ...fleetQueryOptions,
    })),
  });

  const addToFavouritesMutation = useMutation({
    mutationFn: (aircraft: Aircraft) => {
      return addToFavourites(aircraft.id);
    },
    onSuccess: (_, context) => {
      queryClient.invalidateQueries(['aircraft', 'favourites']);
      dispatch(addFavouriteAircraftId({ aircraftId: context.id }));
    },
    onError: (error: Error) => {
      dispatch(addToast({
        payload: {
          title: 'Add to favourites error',
          message: error.message,
          type: ToastTypes.ERROR,
          category: ToastCategories.FLAG,
        }
      }));
    },
  });

  const removeFromFavouritesMutation = useMutation({
    mutationFn: (aircraft: Aircraft) => {
      return removeFromFavourites(aircraft.id);
    },
    onSuccess: (_, context) => {
      queryClient.invalidateQueries(['aircraft', 'favourites']);
      dispatch(removeFavouriteAircraftId({ aircraftId: context.id }));
    },
    onError: (error: Error) => {
      dispatch(addToast({
        payload: {
          title: 'Remove from favourites error',
          message: error.message,
          type: ToastTypes.ERROR,
          category: ToastCategories.FLAG,
        }
      }));
    },
  });

  const fleetStyle = details?.dash_preferences?.fleet_style;

  useEffect(() => {
    const allLoaded = aircraftQueries.every((query) => query.isSuccess);
    if (!allLoaded) return;
    const localAircraft: Aircraft[][] = [];
    const isFirstRun = !hasMountedRef.current;
    const searchChanged = searchText !== prevSearchTextRef.current;
    const serviceabilityChanged = serviceabilityFilters !== prevServiceabilityRef.current;
    const aircraftStatusChanged = aircraftStatusFilters !== prevAircraftStatusRef.current;
    const flightStatusChanged = flightStatusFilters !== prevFlightStatusRef.current;
    const operatorChanged = operatorFilter !== prevOperatorRef.current;
    const typeChanged = typeFilter !== prevTypeRef.current;
    const sortByChanged = sortBy !== prevSortByRef.current;
    const dataChanged = data?.data !== prevData.current;
    const fleetStyleChanged = fleetStyle !== prevFleetStyleRef.current;
    if (
      isFirstRun ||
      searchChanged ||
      serviceabilityChanged ||
      aircraftStatusChanged ||
      flightStatusChanged ||
      operatorChanged ||
      typeChanged ||
      sortByChanged ||
      dataChanged ||
      fleetStyleChanged
    ) {
      operators.forEach((operator, index) => {
        if (aircraftQueries[index].isSuccess) {
          const operatorAircraft = applyAircraftFilters({
            aircrafts: aircraftQueries[index].data?.data ?? [],
            serviceabilityFilters,
            aircraftStatusFilters,
            flightStatusFilters,
            operatorFilter,
            typeFilter,
            searchText,
            sortBy,
          });
          localAircraft.push(operatorAircraft);
        }
      });
      const filteredAircraftIds = localAircraft.flat().map((aircraft) => aircraft.id);
      const test = data?.data?.filter((aircraft: Aircraft) => filteredAircraftIds.includes(aircraft.id));
      hasMountedRef.current = true;
      prevSearchTextRef.current = searchText;
      prevServiceabilityRef.current = serviceabilityFilters;
      prevAircraftStatusRef.current = aircraftStatusFilters;
      prevFlightStatusRef.current = flightStatusFilters;
      prevOperatorRef.current = operatorFilter;
      prevTypeRef.current = typeFilter;
      prevSortByRef.current = sortBy;
      prevData.current = data?.data;
      prevFleetStyleRef.current = fleetStyle;
      setfilteredAircraftQueries(localAircraft);
      setFilteredFavourites(test);
    }
  }, [
    operators,
    searchText,
    aircraftQueries,
    serviceabilityFilters,
    aircraftStatusFilters,
    flightStatusFilters,
    operatorFilter,
    sortBy,
    typeFilter,
    data,
    isSuccess,
    fleetStyle,
  ]);

  const handleSwitchChange = (style: boolean): void => {
    const newStyle = style ? 'light' : 'full';
    const formData = {
      fleet_style: newStyle,
    };
    dispatch(add({ payload: { form: formData, type: 'fleetStyle' } }));
  };

  const handleFavouriteButtonClick = (isFavourited, aircraft): void => {
    if (isFavourited) {
      removeFromFavouritesMutation.mutate(aircraft);
    } else {
      addToFavouritesMutation.mutate(aircraft);
    }
  };
  const renderSimpleAircraftCards = (simpleAircraftResponse): ReactNode => {
    const simpleAircraftData = simpleAircraftResponse.data as SimpleAircraft[];
    return simpleAircraftData.map((aircraft) => {
      return (
        <div key={aircraft.id}>
          <TypedAircraftCard
            aircraftId={aircraft.id}
            preAircraft={aircraft}
            clickable={aircraft.clickable}
            data-test="aircraftCardCol"
            data-testid="aircraftCardCol"
            handleFavouriteButtonClick={(isFavourited, ac): void => handleFavouriteButtonClick(isFavourited, ac)}
          />
        </div>
      );
    });
  };

  const renderFullAircraftCards = (fullAircraftResponse, isFavourite?): ReactNode => {
    const fullAircraftData = fullAircraftResponse as Aircraft[];
    return fullAircraftData?.map((aircraft) => {
      return (
        <div key={aircraft.id}>
          <TypedAircraftCard
            aircraftId={aircraft.id}
            aircraft={aircraft}
            preAircraft={aircraft}
            clickable={aircraft.clickable || isFavourite}
            data-test="aircraftCardCol"
            data-testid="aircraftCardCol"
            isFavourite={isFavourite}
            handleFavouriteButtonClick={(isFavourited, ac): void => handleFavouriteButtonClick(isFavourited, ac)}
          />
        </div>
      );
    });
  };

  const renderSortingControls = (): ReactNode => {
    const sortingMenu = (
      <>
        <Menu>
          <Menu.Item key="asc" onClick={(): void => setSortBy('asc')}>
            {formatMessage({ id: 'menu.registrationAZ' })}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item key="desc" onClick={(): void => setSortBy('desc')}>
            {formatMessage({ id: 'menu.registrationZA' })}
          </Menu.Item>
        </Menu>
      </>
    );
    return (
      <span>
        <SortByLabel>{formatMessage({ id: 'label.sortBy' })}:</SortByLabel>
        <Dropdown overlay={sortingMenu} trigger={['hover']}>
          <span>
            {sortBy === 'asc' ? 'Registration (A-Z)' : 'Registration (Z-A)'}
            <Icon type="caret-down" />
          </span>
        </Dropdown>
      </span>
    );
  };

  const resetFilters = (): void => {
    clearFilters();
    setSearchText('');
    setResetSearch(!resetSearch);
  };

  const renderFilterControls = (): ReactNode => {
    const isStillLoading = aircraftQueries.some((query) => query.isLoading);
    if (isStillLoading) {
      return null;
    }
    const allAircraft = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < operators.length; i++) {
      if (aircraftQueries[i].data) {
        allAircraft.push(...aircraftQueries[i].data?.data);
      }
    }

    if (!hasSetAircraftMap) {
      dispatch(saveAllAircraft({ payload: allAircraft }));
      setHasSetAircraftMap(true);
    }

    const aircraftTypes = allAircraft.map((item) => {
      return item.aircraft_type;
    });
    aircraftTypes.sort((a, b) => (a.manufacturer > b.manufacturer ? 1 : -1));
    const uniqueIds = new Set();
    const uniqueAircraftTypes = aircraftTypes.filter((obj) => {
      if (!uniqueIds.has(obj.id)) {
        uniqueIds.add(obj.id);
        return true;
      }
      return false;
    });
    const TableStyle = details?.dash_preferences?.fleet_style === 'light';

    return (
      <FiltersRow>
        <FilterBar>
          <LeftFilterBarBox>
            <FilterTopRow>
              <FilterText>Filters:</FilterText>
              <TFFilterButton
                id="Serviceability"
                options={[
                  {
                    title: 'Serviceable',
                    count: allAircraft.filter((item) => item.maintenance_status === 'ok').length,
                    value: 'ok',
                    selected: serviceabilityFilters.ok,
                  },
                  {
                    title: 'Unserviceable',
                    value: 'overdue',
                    count: allAircraft.filter((item) => item.maintenance_status === 'overdue').length,
                    selected: serviceabilityFilters.overdue,
                  },
                  {
                    title: 'In tolerance',
                    count: allAircraft.filter((item) => item.maintenance_status === 'critical').length,
                    value: 'critical',
                    selected: serviceabilityFilters.critical,
                  },
                  {
                    title: 'Unknown',
                    count: allAircraft.filter((item) => item.maintenance_status === 'unknown').length,
                    value: 'unknown',
                    selected: serviceabilityFilters.unknown,
                  },
                ]}
                handleClick={(val): void => {
                  updateFilter({ serviceabilityFilters: { ...serviceabilityFilters, ...val } });
                }}
                handleClear={() => {
                  updateFilter({ serviceabilityFilters: DEFAULT_FLEET_FILTERS.serviceabilityFilters });
                }}
                label="Serviceability"
              />
              <TFFilterButton
                id="AircraftStatus"
                options={[
                  {
                    title: 'Active',
                    count: allAircraft.filter((item) => item.billing_status === 'Active').length,
                    value: 'active',
                    selected: aircraftStatusFilters.active,
                  },
                  {
                    title: 'Archived',
                    count: allAircraft.filter((item) => item.billing_status === 'Archived').length,
                    value: 'archived',
                    selected: aircraftStatusFilters.archived,
                  },
                  {
                    title: 'Deleted',
                    count: allAircraft.filter((item) => item.billing_status === 'Deleted').length,
                    value: 'deleted',
                    selected: aircraftStatusFilters.deleted,
                  },
                  {
                    title: 'Demo',
                    count: allAircraft.filter((item) => item.billing_status === 'Demo').length,
                    value: 'demo',
                    selected: aircraftStatusFilters.demo,
                  },
                  {
                    title: 'Locked',
                    count: allAircraft.filter((item) => item.locked === true).length,
                    value: 'locked',
                    selected: aircraftStatusFilters.locked,
                  },
                  {
                    title: 'MEL Only',
                    count: allAircraft.filter((item) => item.billing_status === 'MEL Only').length,
                    value: 'mel only',
                    selected: aircraftStatusFilters['mel only'],
                  },
                  {
                    title: 'Setup',
                    count: allAircraft.filter((item) => item.billing_status === 'Setup').length,
                    value: 'setup',
                    selected: aircraftStatusFilters.setup,
                  },
                ]}
                handleClick={(val): void => {
                  updateFilter({ aircraftStatusFilters: { ...aircraftStatusFilters, ...val } });
                }}
                handleClear={() => {
                  updateFilter({ aircraftStatusFilters: DEFAULT_FLEET_FILTERS.aircraftStatusFilters });
                }}
                label="Aircraft status"
              />
              <TFFilterButton
                id="FlightStatus"
                options={[
                  {
                    title: 'On Ground',
                    count: allAircraft.filter((item) => item.flight_status === 'on_ground').length,
                    value: 'on_ground',
                    selected: flightStatusFilters.on_ground,
                  },
                  {
                    title: 'In flight',
                    count: allAircraft.filter((item) => item.flight_status === 'in_flight').length,
                    value: 'in_flight',
                    selected: flightStatusFilters.in_flight,
                  },
                ]}
                handleClick={(val): void => {
                  updateFilter({ flightStatusFilters: { ...flightStatusFilters, ...val } });
                }}
                handleClear={() => {
                  updateFilter({ flightStatusFilters: DEFAULT_FLEET_FILTERS.flightStatusFilters });
                }}
                label="Flight status"
              />
              <TFFilterButton
                id="Operators"
                options={operators.map((item) => ({
                  title: `${item.name}`,
                  value: item.id,
                  count: allAircraft.filter((aircraft) => aircraft.operator_id === item.id).length,
                  selected: operatorFilter.includes(item.id),
                }))}
                handleClick={(val): void => {
                  const operatorId = Object.keys(val)[0];
                  const hasBeenSelected = Object.values(val)[0];
                  if (hasBeenSelected) {
                    if (!operatorFilter.includes(operatorId)) {
                      updateFilter({ operatorFilter: [...operatorFilter, operatorId] });
                    }
                  } else {
                    updateFilter({ operatorFilter: operatorFilter.filter((id) => id !== operatorId) });
                  }
                }}
                handleClear={(): void => {
                  updateFilter({ operatorFilter: DEFAULT_FLEET_FILTERS.operatorFilter });
                }}
                label="Operators"
                width="330px"
              />
              <TFFilterButton
                id="Types"
                options={uniqueAircraftTypes.map((item) => ({
                  title: `${item.manufacturer} ${item.model}`,
                  value: item.id,
                  count: allAircraft.filter((aircraft) => aircraft.aircraft_type.id === item.id).length,
                  selected: typeFilter.includes(item.id),
                }))}
                handleClick={(val): void => {
                  const typeId = Object.keys(val)[0];
                  const hasBeenSelected = Object.values(val)[0];
                  if (hasBeenSelected) {
                    if (!typeFilter.includes(typeId)) {
                      updateFilter({ typeFilter: [...typeFilter, typeId] });
                    }
                  } else {
                    updateFilter({ typeFilter: typeFilter.filter((id) => id !== typeId) });
                  }
                }}
                handleClear={() => {
                  updateFilter({ typeFilter: DEFAULT_FLEET_FILTERS.typeFilter });
                }}
                label="Types"
                width="330px"
              />
              <SearchWrapper>
                <Search
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  placeholder={formatMessage({ id: 'form.placeholder.searchAircraft' })}
                  onClear={(): void => setSearchText('')}
                  reset={resetSearch}
                />
              </SearchWrapper>
            </FilterTopRow>
            <ClearAllFiltersLink
              type="button"
              disabled={!isFiltered(filters, searchText)}
              onClick={resetFilters}
            >
              <Icon type="close" />
              {formatMessage({ id: 'text.clearAllFilters' })}
            </ClearAllFiltersLink>
          </LeftFilterBarBox>
          <RightFilterBarBox>
            {renderSortingControls()}
            {/* the next line explicity checks for false, as it is nil by default */}
            {details?.dash_preferences?.allow_full_fleet_style !== false && (
              <TableGridToggle handleToggleChange={handleSwitchChange} tableStyleSelected={TableStyle} />
            )}
          </RightFilterBarBox>
        </FilterBar>
      </FiltersRow>
    );
  };

  const renderFavourites = (): ReactNode => {
    if (filteredFavourites && filteredFavourites?.length === 0) {
      return null;
    }
    if (isSuccess && data?.data.length > 0) {
      return (
        <OperatorDiv>
          <Row>
            <h3>{formatMessage({ id: 'text.favourites' })}</h3>
          </Row>
          <CardsDiv>{renderFullAircraftCards(filteredFavourites || data?.data, true)}</CardsDiv>
        </OperatorDiv>
      );
    }
    return null;
  };

  function areArraysEmpty(arr): boolean {
    for (const subArray of arr) {
      if (subArray.length !== 0) {
        return false;
      }
    }
    return true;
  }
  let filtersAreEmpty = false;

  operators.forEach((operator, index) => {
    if (aircraftQueries[index].isSuccess && filteredAircraftQueries) {
      filtersAreEmpty = areArraysEmpty(filteredAircraftQueries);
    }
  });

  return (
    <PageHeaderWrapper icon={FleetIcon} title={formatMessage({ id: 'title.fleet' })}>
      <div>{renderFilterControls()}</div>
      {filtersAreEmpty ? (
        <EmptyState
          image={EmptyStateAircraft}
          text="We couldn't find any matching aircraft"
          subText="Try adjusting your filters or searching with another term."
          button="Clear all"
          buttonAction={resetFilters}
        />
      ) : (
        <>
          {renderFavourites()}
          {operators.map((operator, index) => (
            <>
              {filteredAircraftQueries && filteredAircraftQueries[index].length === 0 ? (
                <></>
              ) : (
                <OperatorDiv>
                  <Row>
                    <h3>{operator.name}</h3>
                  </Row>
                  {simpleAircraftQueries[index].isSuccess && aircraftQueries[index].isLoading && (
                    <CardsDiv>{renderSimpleAircraftCards(simpleAircraftQueries[index].data)}</CardsDiv>
                  )}
                  <CardsDiv>
                    {aircraftQueries[index].isSuccess &&
                      renderFullAircraftCards(
                        filteredAircraftQueries ? filteredAircraftQueries[index] : aircraftQueries[index]?.data?.data,
                      )}
                  </CardsDiv>
                </OperatorDiv>
              )}
            </>
          ))}
        </>
      )}
    </PageHeaderWrapper>
  );
};

export default Fleet;
